import { vendorStore } from "../vendors";
import { VENDOR_DOMAINS } from "../vendors/logos";
import { normalizeVendor } from "../csv/normalizeVendor";

// Wave 14 — fuzzy vendor recognition. OCR on messy receipts often produces
// small edit-distance garbles ("Strpe", "Amzaon", "Stipe") of known vendor
// names. Rather than asking the user to fix the typo by hand, compare each
// top-of-receipt candidate line against a known-vendor corpus (the org's
// own vendor history + a seeded dictionary of common B2B vendors) and pick
// the highest-scoring match when we're confident enough.

export interface VendorMatch {
  canonicalName: string;
  normalizedName: string;
  vendorId: string | null; // only set when source === "history"
  source: "history" | "seeded";
  score: number; // 0..1, higher = better
}

// Score threshold below which we don't surface the match at all. 0.6 was
// chosen empirically: "Strpe" vs "stripe" scores ~0.83, "Amzaon" vs
// "amazon" ~0.83, but "Different Vendor" vs "stripe" sits around 0.20 —
// plenty of headroom.
const MIN_SCORE = 0.6;

interface KnownVendor {
  canonicalName: string;
  normalizedName: string;
  vendorId: string | null;
  source: "history" | "seeded";
}

// Seeded canonical names — map normalized keys from VENDOR_DOMAINS to a
// display-ready canonical form. The dictionary is keyed by normalized
// strings (`awsamazon`, `amazon web services`), so reverse-engineer a
// reasonable display form. Acronyms stay upper-case; everything else is
// title-cased. Used only when the user hasn't got their own vendor with
// the same normalized name.
const SEEDED_CANONICAL_OVERRIDES: Record<string, string> = {
  aws: "AWS",
  awsamazon: "AWS",
  "amazon web services": "Amazon Web Services",
  gsuite: "Google Workspace",
  "google workspace": "Google Workspace",
  workspace: "Google Workspace",
  m365: "Microsoft 365",
  "microsoft 365": "Microsoft 365",
  "google cloud": "Google Cloud",
  openai: "OpenAI",
  github: "GitHub",
  hubspot: "HubSpot",
  posthog: "PostHog",
  pagerduty: "PagerDuty",
  sendgrid: "SendGrid",
  mailchimp: "Mailchimp",
  airbnb: "Airbnb",
  wework: "WeWork",
  officedepot: "Office Depot",
  "office depot": "Office Depot",
  paypal: "PayPal",
};

function titleCase(normalized: string): string {
  return normalized
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function seededCanonicalFor(normalized: string): string {
  return SEEDED_CANONICAL_OVERRIDES[normalized] ?? titleCase(normalized);
}

/**
 * Build the known-vendor corpus the fuzzy matcher scores against. The
 * user's own vendor history wins over seeded dictionary entries — if an
 * org calls their corporate Stripe account "Stripe Payments", we want to
 * match to that canonical name, not the generic "Stripe".
 */
function buildCorpus(): KnownVendor[] {
  const byNormalized = new Map<string, KnownVendor>();

  // History first — gets priority over seeded entries.
  try {
    for (const v of vendorStore.list()) {
      const normalized = (v.normalizedName ?? "").trim();
      if (!normalized) continue;
      byNormalized.set(normalized, {
        canonicalName: v.canonicalName,
        normalizedName: normalized,
        vendorId: v.id,
        source: "history",
      });
    }
  } catch {
    // `vendorStore.list()` can throw if storage is wedged. Fuzzy match is
    // a nice-to-have; swallow + fall through to seeded-only.
  }

  for (const key of Object.keys(VENDOR_DOMAINS)) {
    const normalized = normalizeVendor(key);
    if (!normalized) continue;
    if (byNormalized.has(normalized)) continue; // history wins
    byNormalized.set(normalized, {
      canonicalName: seededCanonicalFor(normalized),
      normalizedName: normalized,
      vendorId: null,
      source: "seeded",
    });
  }

  return [...byNormalized.values()];
}

/**
 * Standard O(n*m) Levenshtein distance. Kept inline — no npm dep for what
 * is a textbook dynamic-programming loop. Short-circuits when either
 * string is empty.
 */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const m = a.length;
  const n = b.length;
  // Two-row rolling buffer — we only ever read the previous row.
  let prev = new Array<number>(n + 1);
  let curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    const ac = a.charCodeAt(i - 1);
    for (let j = 1; j <= n; j++) {
      const cost = ac === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // deletion
        curr[j - 1] + 1, // insertion
        prev[j - 1] + cost, // substitution
      );
    }
    // Swap rows.
    const tmp = prev;
    prev = curr;
    curr = tmp;
  }
  return prev[n];
}

function scorePair(candidate: string, known: string): number {
  const maxLen = Math.max(candidate.length, known.length);
  if (maxLen === 0) return 0;
  const d = levenshtein(candidate, known);
  return 1 - d / maxLen;
}

/**
 * Score a list of candidate OCR lines against the known-vendor corpus and
 * return the best match when it clears MIN_SCORE, or null otherwise.
 * Ties are broken by source (history wins over seeded), so an org's
 * preferred canonical for "Stripe" (e.g. "Stripe Payments") takes
 * precedence over the seeded dictionary's "Stripe".
 *
 * `ocrLines` should be the top of the receipt — typically the first
 * ~6 lines. Passing the entire receipt dilutes the match because any
 * stray "amount" / "balance" label could accidentally edit-distance
 * close to something in the dictionary.
 */
export function matchVendor(ocrLines: string[]): VendorMatch | null {
  const corpus = buildCorpus();
  if (corpus.length === 0) return null;

  // Normalize each candidate; skip empties. Cap at 6 lines.
  const candidates = ocrLines
    .slice(0, 6)
    .map((l) => normalizeVendor(l))
    .filter((n) => n.length >= 2);

  if (candidates.length === 0) return null;

  let best: VendorMatch | null = null;
  for (const cand of candidates) {
    for (const kv of corpus) {
      const score = scorePair(cand, kv.normalizedName);
      if (score < MIN_SCORE) continue;
      if (
        !best ||
        score > best.score ||
        (score === best.score &&
          best.source === "seeded" &&
          kv.source === "history")
      ) {
        best = {
          canonicalName: kv.canonicalName,
          normalizedName: kv.normalizedName,
          vendorId: kv.vendorId,
          source: kv.source,
          score,
        };
      }
    }
  }

  return best;
}
